import { API_CONFIG } from "../../../utils/apiConfig";
import { TokenManager } from "../../../utils/storage/tokenmanager.storage";
import { AssistantStreamEvent } from "../interfaces/assistant.interface";

interface StreamChatParams {
  message: string;
  sessionId: string | null;
  currentPath: string;
  onEvent: (event: AssistantStreamEvent) => void;
  signal?: AbortSignal;
}

/**
 * The assistant streams Server-Sent Events, which axios cannot consume in the browser,
 * so this service talks to the API with `fetch` directly. Auth is optional — visitors
 * get the assistant too, just without account-scoped answers.
 */
class AssistantService {
  private static instance: AssistantService;

  static getInstance(): AssistantService {
    if (!AssistantService.instance) {
      AssistantService.instance = new AssistantService();
    }
    return AssistantService.instance;
  }

  private url(path: string): string {
    return `${API_CONFIG.BASE_URL.replace(/\/$/, "")}/assistant${path}`;
  }

  private headers(): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    const token = TokenManager.getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  }

  async getStatus(): Promise<boolean> {
    try {
      const response = await fetch(this.url("/status"), {
        headers: this.headers(),
      });
      if (!response.ok) return false;
      const body = await response.json();
      return Boolean(body?.data?.enabled);
    } catch {
      return false;
    }
  }

  async streamChat({
    message,
    sessionId,
    currentPath,
    onEvent,
    signal,
  }: StreamChatParams): Promise<void> {
    const response = await fetch(this.url("/chat"), {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify({ message, sessionId, currentPath }),
      signal,
    });

    if (!response.ok || !response.body) {
      // Validation and rate-limit failures come back as ordinary JSON, not SSE.
      let detail = "The assistant is unavailable right now. Please try again.";
      try {
        const body = await response.json();
        if (body?.message) detail = body.message;
      } catch {
        /* keep the default */
      }
      onEvent({ type: "error", message: detail });
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // SSE frames are separated by a blank line; a partial frame stays buffered.
      const frames = buffer.split("\n\n");
      buffer = frames.pop() ?? "";

      for (const frame of frames) {
        for (const line of frame.split("\n")) {
          if (!line.startsWith("data:")) continue; // ignore ": ping" heartbeats
          const payload = line.slice(5).trim();
          if (!payload) continue;
          try {
            onEvent(JSON.parse(payload) as AssistantStreamEvent);
          } catch {
            /* ignore a malformed frame rather than killing the stream */
          }
        }
      }
    }
  }

  /** Replays a stored conversation so a reload does not appear to lose the chat. */
  async fetchHistory(
    sessionId: string,
  ): Promise<{ role: "user" | "assistant"; text: string }[]> {
    try {
      const response = await fetch(this.url(`/sessions/${sessionId}/history`), {
        headers: this.headers(),
      });
      if (!response.ok) return [];
      const body = await response.json();
      return body?.data?.messages ?? [];
    } catch {
      return [];
    }
  }

  async resetSession(sessionId: string): Promise<void> {
    try {
      await fetch(this.url(`/sessions/${sessionId}`), {
        method: "DELETE",
        headers: this.headers(),
      });
    } catch {
      /* clearing the server copy is best-effort */
    }
  }
}

export const assistantService = AssistantService.getInstance();
